import { useId, useState } from "react";
import type { FocusEvent, InputHTMLAttributes } from "react";
import classNames from "classnames";

// Accepts every normal <input> prop (name, onChange, maxLength, autoComplete...).
// "title" is the floating label text.
interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "title" | "dir"> {
    title?: string; // Leave it out for compact inline fields
    dir?: "rtl" | "ltr"; // Direction of the text and the label (Persian forms are rtl)
}

const InputField = ({
                        title,
                        type = "text",
                        dir = "rtl",
                        className,
                        value,
                        disabled,
                        onFocus,
                        onBlur,
                        ...inputProps
                    }: InputFieldProps) => {
    const inputId = useId(); // Links the label to the input
    const [isFocused, setIsFocused] = useState(false);

    const isRtl = dir === "rtl";
    const hasValue = String(value ?? "") !== "";
    // The label floats up while typing or when the field already has a value
    const isLabelRaised = isFocused || hasValue;

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur?.(event);
    };

    return (
        <div className={classNames("relative bg-transparent", className)} dir={dir}>
            <input
                {...inputProps}
                id={inputId}
                type={type}
                disabled={disabled}
                value={value ?? ""} // Always controlled, never undefined
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={classNames(
                    "w-full px-4 pt-5 pb-3 text-xl xl:text-md no-spinner outline-0 border-2 rounded-md appearance-none transition-all disabled:bg-gray-200 peer",
                    isRtl ? "text-right" : "text-left",
                    isFocused && !disabled ? "border-light-primary" : "border-gray-200"
                )}
            />

            {title && (
                <label
                    htmlFor={inputId}
                    className={classNames(
                        // pointer-events-none lets clicks on the label reach the input
                        "absolute text-gray-400 text-lg xl:text-md transition-all px-1 pointer-events-none",
                        isRtl ? "right-4" : "left-4",
                        isLabelRaised
                            ? "top-1 text-md xl:text-sm text-light-primary"
                            : "top-1/3 -translate-y-1/2",
                        disabled ? "hidden" : "block"
                    )}
                >
                    {title}
                </label>
            )}
        </div>
    );
};

export default InputField;